import type { JSONContent } from '@tiptap/core';

/**
 * Marker format for inline text colors in markdown:
 *   {{color:value}}text{{/color}}
 *
 * Similar to {{popover:id}}text{{/popover}} and {{icon:fa:name:color}}.
 */

const COLOR_MARKER_REGEX = /\{\{color:([^}]+)\}\}(.*?)\{\{\/color\}\}/g;

// --- Save path: textStyle color marks → text markers ---

/**
 * Recursively converts textStyle color marks to {{color:value}}text{{/color}} markers.
 * This preserves inline text colors through markdown serialization.
 */
export function convertTextColorsToMarkers(node: JSONContent): JSONContent {
  if (node.type === 'text' && node.text && node.marks) {
    const colorMark = node.marks.find((m) => m.type === 'textStyle' && m.attrs?.['color']);
    if (colorMark) {
      const color = colorMark.attrs!['color'] as string;
      const otherMarks = node.marks.filter((m) => m !== colorMark);
      // Remove empty textStyle marks (no remaining attrs)
      const cleanedMarks = otherMarks.filter(
        (m) =>
          m.type !== 'textStyle' ||
          (m.attrs && Object.keys(m.attrs).some((k) => m.attrs![k] !== null && k !== 'color')),
      );
      return {
        type: 'text',
        text: `{{color:${color}}}${node.text}{{/color}}`,
        ...(cleanedMarks.length > 0 && { marks: cleanedMarks }),
      };
    }
    return node;
  }

  if (node.content) {
    return {
      ...node,
      content: node.content.map(convertTextColorsToMarkers),
    };
  }

  return node;
}

// --- Load path: text markers → textStyle color marks ---

/**
 * Recursively processes a node to convert {{color:value}}text{{/color}} markers
 * to textStyle color marks on text nodes.
 */
export function processNodeForTextColors(node: JSONContent): JSONContent {
  if (node.content) {
    return {
      ...node,
      content: processContentForTextColors(node.content),
    };
  }

  return node;
}

function processContentForTextColors(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const parts = splitTextWithColors(node.text, node.marks);
      result.push(...parts);
    } else if (node.content) {
      result.push({
        ...node,
        content: processContentForTextColors(node.content),
      });
    } else {
      result.push(node);
    }
  }

  return result;
}

function splitTextWithColors(text: string, marks?: JSONContent['marks']): JSONContent[] {
  const result: JSONContent[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(COLOR_MARKER_REGEX)) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      result.push({ type: 'text', text: beforeText, ...(marks && { marks }) });
    }

    const color = match[1];
    const coloredText = match[2];

    const colorMark = { type: 'textStyle', attrs: { color } };
    const combinedMarks = marks ? [...marks, colorMark] : [colorMark];

    result.push({
      type: 'text',
      text: coloredText,
      marks: combinedMarks,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    result.push({ type: 'text', text: remainingText, ...(marks && { marks }) });
  }

  // If no color markers were found, return original text node
  if (result.length === 0) {
    result.push({ type: 'text', text, ...(marks && { marks }) });
  }

  return result;
}
